// SOURCE OF TRUTH: sync this file across bynova-product-studio,
// bynova-storefront-widget, bynova-workers when modifying.

import {
  WarpPresetParams,
  WarpStyle,
  WarpTransformOptions,
  WarpTransformPoint,
} from "../interfaces";

// Matches opentype.js PathCommand — defined locally to avoid importing opentype here.
export type GlyphPathCommand =
  | { type: "M"; x: number; y: number }
  | { type: "L"; x: number; y: number }
  | { type: "C"; x1: number; y1: number; x2: number; y2: number; x: number; y: number }
  | { type: "Q"; x1: number; y1: number; x: number; y: number }
  | { type: "Z" };

type Point2D = WarpTransformPoint;

interface ArcLengthSegment {
  p0: Point2D; p1: Point2D; p2: Point2D; p3: Point2D;
  samplePoints: Point2D[];  // 101 entries
  cumLengths: number[];     // 101 entries, cumLengths[0] = 0
  totalLength: number;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const p = (x: number, y: number): Point2D => ({ x, y });

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPoint2D(value: unknown): value is Point2D {
  return (
    isRecord(value) &&
    typeof value.x === "number" &&
    typeof value.y === "number" &&
    Number.isFinite(value.x) &&
    Number.isFinite(value.y)
  );
}

function cubicBez(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

function cubicBezPoint(a: Point2D, b: Point2D, c: Point2D, d: Point2D, t: number): Point2D {
  return {
    x: cubicBez(a.x, b.x, c.x, d.x, t),
    y: cubicBez(a.y, b.y, c.y, d.y, t),
  };
}

function dist(a: Point2D, b: Point2D): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

// Arc-length lookup table (101 samples for arc-length parameterization)
function buildArcLengthTable(
  p0: Point2D,
  p1: Point2D,
  p2: Point2D,
  p3: Point2D,
): ArcLengthSegment {
  const SAMPLES = 101;
  const samplePoints: Point2D[] = [];
  const cumLengths: number[] = [0];

  for (let i = 0; i < SAMPLES; i++) {
    const t = i / (SAMPLES - 1);
    samplePoints.push(cubicBezPoint(p0, p1, p2, p3, t));
    if (i > 0) {
      cumLengths.push(cumLengths[i - 1] + dist(samplePoints[i - 1], samplePoints[i]));
    }
  }

  return { p0, p1, p2, p3, samplePoints, cumLengths, totalLength: cumLengths[SAMPLES - 1] };
}

function getPointAtLengthRatio(seg: ArcLengthSegment, ratio: number): Point2D {
  if (seg.totalLength === 0) return seg.p0;

  const target = Math.max(0, Math.min(1, ratio)) * seg.totalLength;
  let lo = 0;
  let hi = seg.cumLengths.length - 1;

  while (lo < hi - 1) {
    const mid = (lo + hi) >> 1;
    if (seg.cumLengths[mid] < target) lo = mid;
    else hi = mid;
  }

  const span = seg.cumLengths[hi] - seg.cumLengths[lo];
  const localT = span > 0 ? (target - seg.cumLengths[lo]) / span : 0;
  const a = seg.samplePoints[lo];
  const b = seg.samplePoints[hi];

  return {
    x: a.x + (b.x - a.x) * localT,
    y: a.y + (b.y - a.y) * localT,
  };
}

// ---------------------------------------------------------------------------
// EnvelopeMapper
// ---------------------------------------------------------------------------

/**
 * Two-rail cubic Bezier envelope distortion.
 *
 * Rail construction:
 *   top.left    = cubic(p[0], p[0],  p[6], p[1])
 *   top.right   = cubic(p[1], p[7],  p[2], p[2])
 *   bottom.left = cubic(p[5], p[5],  p[8], p[4])
 *   bottom.right= cubic(p[4], p[9],  p[3], p[3])
 */
export class EnvelopeMapper {
  private readonly segTopLeft:     ArcLengthSegment;
  private readonly segTopRight:    ArcLengthSegment;
  private readonly segBottomLeft:  ArcLengthSegment;
  private readonly segBottomRight: ArcLengthSegment;

  constructor(
    private readonly totalWidth: number,
    private readonly ascenderAbs: number,
    private readonly glyphHeight: number,
    transformOptions: WarpTransformOptions,
  ) {
    // Denormalize points from [0,1] relative to layer bounds into local group
    // space where the group center is at (0,0).
    const toAbs = (pt: Point2D): Point2D => ({
      x: pt.x * totalWidth  - totalWidth  / 2,
      y: pt.y * glyphHeight - glyphHeight / 2,
    });

    const [p0, p1, p2, p3, p4, p5, p6, p7, p8, p9] = transformOptions.points.map(toAbs);

    this.segTopLeft     = buildArcLengthTable(p0, p0, p6, p1);
    this.segTopRight    = buildArcLengthTable(p1, p7, p2, p2);
    this.segBottomLeft  = buildArcLengthTable(p5, p5, p8, p4);
    this.segBottomRight = buildArcLengthTable(p4, p9, p3, p3);
  }

  mapPoint(px: number, py: number): Point2D {
    const u = Math.max(0, Math.min(1, px / this.totalWidth));
    const v = Math.max(-0.2, Math.min(1.2, (py + this.ascenderAbs) / this.glyphHeight));

    let topPoint: Point2D;
    let bottomPoint: Point2D;

    if (u <= 0.5) {
      const uLeft = u / 0.5; // remap [0, 0.5] → [0, 1]
      topPoint    = getPointAtLengthRatio(this.segTopLeft,    uLeft);
      bottomPoint = getPointAtLengthRatio(this.segBottomLeft, uLeft);
    } else {
      const uRight = (u - 0.5) / 0.5; // remap [0.5, 1] → [0, 1]
      topPoint    = getPointAtLengthRatio(this.segTopRight,    uRight);
      bottomPoint = getPointAtLengthRatio(this.segBottomRight, uRight);
    }

    return {
      x: (1 - v) * topPoint.x + v * bottomPoint.x,
      y: (1 - v) * topPoint.y + v * bottomPoint.y,
    };
  }
}

// ---------------------------------------------------------------------------
// Preset definitions — array format [0,1] normalized, order matches TRANSFORM_POINT_INDEX
// [topLeft, topMiddle, topRight, bottomRight, bottomMiddle, bottomLeft,
//  topLeftHandle, topRightHandle, bottomLeftHandle, bottomRightHandle]
// ---------------------------------------------------------------------------

const PRESET_TRANSFORM_POINTS: Record<WarpStyle, Point2D[]> = {
  [WarpStyle.Rectangle]: [
    p(0, 0), p(0.5, 0), p(1, 0), p(1, 1), p(0.5, 1), p(0, 1),
    p(0.25, 0), p(0.75, 0), p(0.25, 1), p(0.75, 1),
  ],
  [WarpStyle.Arch]: [
    p(0, 0.2), p(0.5, 0), p(1, 0.2), p(1, 1), p(0.5, 1), p(0, 1),
    p(0.25, 0), p(0.75, 0), p(0.25, 1), p(0.75, 1),
  ],
  [WarpStyle.BowTie]: [
    p(0, 0), p(0.5, 0.35), p(1, 0), p(1, 1), p(0.5, 0.65), p(0, 1),
    p(0.25, 0.35), p(0.75, 0.35), p(0.25, 0.65), p(0.75, 0.65),
  ],
  [WarpStyle.Bubble]: [
    p(0, 0.2), p(0.5, 0), p(1, 0.2), p(1, 0.8), p(0.5, 1), p(0, 0.8),
    p(0.25, 0), p(0.75, 0), p(0.25, 1), p(0.75, 1),
  ],
  [WarpStyle.Wave]: [
    p(0, 0), p(0.5, 0.15), p(1, 0.3), p(1, 1), p(0.5, 0.85), p(0, 0.7),
    p(0.25, 0.05), p(0.75, 0.25), p(0.25, 0.75), p(0.75, 0.95),
  ],
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function parseTransformOptions(
  value: WarpTransformOptions | null | undefined,
): WarpTransformOptions | null {
  if (!isRecord(value)) return null;
  const points = Array.isArray(value.points)
    ? value.points.filter(isPoint2D)
    : undefined;
  return points && points.length >= 10 ? { points: points.slice(0, 10) } : null;
}

/**
 * Generate the 10 control points for a given style and optional bend parameter.
 * bend=0 → flat (rectangle), bend=1 → full preset shape.
 */
export function generatePresetPoints(
  style: WarpStyle,
  params?: WarpPresetParams,
): Point2D[] {
  const preset = PRESET_TRANSFORM_POINTS[style] ?? PRESET_TRANSFORM_POINTS[WarpStyle.Rectangle];
  const bend = Math.max(0, Math.min(1, params?.bend ?? 1));

  if (bend === 1) return [...preset];

  const flat = PRESET_TRANSFORM_POINTS[WarpStyle.Rectangle];
  return preset.map((pt, i) => ({
    x: flat[i].x + (pt.x - flat[i].x) * bend,
    y: flat[i].y + (pt.y - flat[i].y) * bend,
  }));
}

export function getDefaultTransformOptions(
  style: WarpStyle,
  params?: WarpPresetParams,
): WarpTransformOptions {
  return {
    points: generatePresetPoints(style, params),
  };
}

/**
 * Transforms all coordinate pairs in an opentype glyph's path commands using
 * the provided warp function. Returns a ready-to-use SVG path string whose
 * coordinates are already in group-local space.
 *
 * Note: warping Bézier control points is an approximation. For moderate warp
 * shapes the error is not visible; for extreme shapes minor contour deviation
 * may occur.
 */
export function warpPathCommands(
  commands: GlyphPathCommand[],
  warpFn: (px: number, py: number) => Point2D,
): string {
  let d = "";
  for (const cmd of commands) {
    switch (cmd.type) {
      case "M": {
        const { x, y } = warpFn(cmd.x, cmd.y);
        d += `M ${x.toFixed(2)} ${y.toFixed(2)} `;
        break;
      }
      case "L": {
        const { x, y } = warpFn(cmd.x, cmd.y);
        d += `L ${x.toFixed(2)} ${y.toFixed(2)} `;
        break;
      }
      case "C": {
        const p1 = warpFn(cmd.x1, cmd.y1);
        const p2 = warpFn(cmd.x2, cmd.y2);
        const ep = warpFn(cmd.x, cmd.y);
        d += `C ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} ${p2.x.toFixed(2)} ${p2.y.toFixed(2)} ${ep.x.toFixed(2)} ${ep.y.toFixed(2)} `;
        break;
      }
      case "Q": {
        const p1 = warpFn(cmd.x1, cmd.y1);
        const ep = warpFn(cmd.x, cmd.y);
        d += `Q ${p1.x.toFixed(2)} ${p1.y.toFixed(2)} ${ep.x.toFixed(2)} ${ep.y.toFixed(2)} `;
        break;
      }
      case "Z":
        d += "Z ";
        break;
    }
  }
  return d.trim();
}
